/**
 * `/performance/:tab?`: one range picker in the header and five report tabs
 * (Summary, By link, By referrer, By country, By page) on `PerformanceData`.
 */
import { PageHeader } from "@nt/ui/page-header";
import { PillTabPanel, PillTabs } from "@nt/ui/pill-tabs";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";

import { type QueryLike, QueryView } from "@/components/query-view";
import { RangePicker, useRangeDays } from "@/components/range-picker";
import { useGet } from "@/lib/queries";
import type { OverviewData, PerformanceData } from "@/lib/types";
import { ActivityChart } from "@/pages/performance/activity-chart";
import { clickRetentionNote } from "@/pages/performance/format";
import { KpiTiles } from "@/pages/performance/kpi-tiles";
import { ReportTable } from "@/pages/performance/report-table";
import { SeriesTable } from "@/pages/performance/series-table";
import { usePerformance } from "@/pages/performance/use-performance";

export const PERFORMANCE_TABS = [
  { value: "summary", label: "Summary" },
  { value: "links", label: "By link" },
  { value: "referrers", label: "By referrer" },
  { value: "countries", label: "By country" },
  { value: "pages", label: "By page" },
] as const;

export type PerformanceTab = (typeof PERFORMANCE_TABS)[number]["value"];

const isTab = (v: string | undefined): v is PerformanceTab =>
  PERFORMANCE_TABS.some((t) => t.value === v);

/** `/performance` is Summary; every other tab has its own `/performance/:tab` URL. */
const tabPath = (tab: PerformanceTab) =>
  tab === "summary" ? "/performance" : `/performance/${tab}`;

/** The tab row and the open tab, for one range's report. */
export function PerformanceView({
  tab,
  performance,
  pendingCents,
}: {
  tab: PerformanceTab;
  performance: QueryLike<PerformanceData>;
  /** From the overview read; null while it is unknown. */
  pendingCents: number | null;
}) {
  const navigate = useNavigate();
  const { search } = useLocation();
  return (
    <div className="flex flex-col gap-4">
      <PillTabs
        aria-label="Reports"
        idPrefix="performance"
        testIdPrefix="performance-tab"
        items={PERFORMANCE_TABS}
        value={tab}
        onChange={(v) => {
          // Keep `?days=` so the range survives a tab change.
          if (isTab(v)) navigate({ pathname: tabPath(v), search });
        }}
      />
      <PillTabPanel
        idPrefix="performance"
        value={tab}
        className="mt-0 flex flex-col gap-6"
      >
        <QueryView query={performance} what="your performance" rows={6}>
          {(data) => {
            const note = clickRetentionNote(data.days, data.clickRetentionDays);
            return (
              <>
                {note ? (
                  <p
                    data-testid="click-retention-note"
                    className="text-sm text-muted-foreground"
                  >
                    {note}
                  </p>
                ) : null}
                {tab === "summary" ? (
                  <>
                    <KpiTiles data={data} pendingCents={pendingCents} />
                    <ActivityChart data={data} />
                    <SeriesTable data={data} />
                  </>
                ) : (
                  <ReportTable data={data} report={tab} />
                )}
              </>
            );
          }}
        </QueryView>
      </PillTabPanel>
    </div>
  );
}

export function Performance() {
  const { tab: param } = useParams();
  const { search } = useLocation();
  const [days, setDays] = useRangeDays();
  const performance = usePerformance(days);
  const overview = useGet<OverviewData>("overview", "/api/partner/overview");
  if (param !== undefined && (!isTab(param) || param === "summary")) {
    return <Navigate to={{ pathname: "/performance", search }} replace />;
  }
  const tab: PerformanceTab = isTab(param) ? param : "summary";
  return (
    <>
      <PageHeader
        title="Performance"
        description="Clicks, sign-ups and fees from your links."
        actions={<RangePicker value={days} onChange={setDays} />}
      />
      <PerformanceView
        tab={tab}
        performance={performance}
        pendingCents={overview.data?.balance.pendingCents ?? null}
      />
    </>
  );
}
